import { matchPath } from 'react-router-dom'

import { AllLocales, type Locale } from '@global/locale/Locale'

import { type LayoutDefinition } from '@hooks/useLayoutVariant'

import { type RouteConfig } from './RenderRoutes'

const stripLocaleFromPathname = (pathname: string): string => {
  const [maybeLocale, ...rest] = pathname.split('/').filter(Boolean)

  return AllLocales.includes(maybeLocale as Locale) ? `/${rest.join('/')}` : pathname
}

const normalizePath = (path: string): string => {
  const normalizedPath = `/${path}`.replace(/\/+/g, '/')

  return normalizedPath.length > 1 ? normalizedPath.replace(/\/$/, '') : '/'
}

const getRoutePath = (parentPath: string, routePath?: string): string => {
  if (!routePath) return parentPath

  return routePath.startsWith('/')
    ? normalizePath(routePath)
    : normalizePath(`${parentPath}/${routePath}`)
}

const routeMatchesPathname = (
  routePath: string,
  route: RouteConfig<string>,
  pathname: string
): boolean => {
  const routeHasChildren = Boolean(route.children?.length)

  return Boolean(matchPath({ path: routePath, end: !routeHasChildren }, pathname))
}

const findRouteLayout = (
  routes: RouteConfig<string>[],
  pathname: string,
  parentPath = '/'
): LayoutDefinition | undefined =>
  routes.reduce<LayoutDefinition | undefined>((matchedLayout, route) => {
    if (route.condition === false) return matchedLayout
    const routePath = getRoutePath(parentPath, route.path)
    const routeLayout =
      route.layout && routeMatchesPathname(routePath, route, pathname)
        ? route.layout
        : matchedLayout

    return route.children
      ? findRouteLayout(route.children, pathname, routePath) ?? routeLayout
      : routeLayout
  }, undefined)

export const getMatchedRouteLayout = (
  routes: RouteConfig<string>[],
  pathname: string
): LayoutDefinition | undefined => {
  const normalizedPathname = normalizePath(stripLocaleFromPathname(pathname))

  return findRouteLayout(routes, normalizedPathname)
}
